import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, ShieldCheck, XCircle, ExternalLink, FileText, BookOpen, X, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import api, { formatApiError } from "../lib/api";
import { IMAGES } from "../lib/site";
import { useAuth } from "../context/AuthContext";

const TAILLES = ["XS", "S", "M", "L", "XL", "XXL"];

const DOC_CHECK_TEXTS = {
  reglement_interieur: "J'ai pris connaissance du règlement intérieur du Club Cycliste Le Boulou et j'accepte ses dispositions.",
  fiche_sante: "J'ai pris connaissance de la fiche santé et informations importantes et je confirme avoir communiqué au club les informations nécessaires concernant la santé et la sécurité de mon enfant.",
  autorisation_parentale: "Je confirme avoir pris connaissance de l'autorisation parentale et j'autorise mon enfant à participer aux activités du Club Cycliste Le Boulou dans les conditions prévues par ce document.",
};

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function fmtDateFr(v) {
  try {
    return new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric" }).format(new Date(v));
  } catch {
    return v;
  }
}
const FFC_URL = "https://compte.ffc.fr/login/?ReturnUrl=https%3A%2F%2Flicence.ffc.fr%2F";
const FSGT_URL = "https://monespace.fsgt.org/auth/login";

const STEPS = ["Mes informations", "Mon adhésion", "Ma licence", "Mon équipement", "Récapitulatif", "Documents", "Paiement"];

const eur = (cents) => `${(cents / 100).toFixed(2)} €`.replace(".", ",");

const inputCls =
  "w-full border border-navy/20 bg-white px-4 py-3.5 text-sm text-navy placeholder:text-steel focus:outline-none focus:border-rouge transition-colors duration-300";
const labelCls = "block font-heading uppercase tracking-wider text-xs text-navy mb-2";

function StepHead({ num, title, children }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-4 mb-4">
        <span className="flex h-9 w-9 items-center justify-center bg-rouge text-white font-heading font-semibold">{num}</span>
        <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl sm:text-3xl">{title}</h2>
      </div>
      {children && <div className="text-navy/70 text-sm sm:text-base leading-relaxed max-w-3xl">{children}</div>}
    </div>
  );
}

function RadioCard({ checked, onSelect, title, sub, price, testId }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={testId}
      className={`text-left border p-5 transition-[border-color,background-color] duration-300 ${
        checked ? "border-rouge bg-rouge/5" : "border-navy/15 bg-white hover:border-rouge/50"
      }`}
    >
      <span className="flex items-center gap-3">
        <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${checked ? "border-rouge" : "border-steel"}`}>
          {checked && <span className="h-2 w-2 rounded-full bg-rouge" />}
        </span>
        <span className="font-heading uppercase font-semibold tracking-tight text-navy">{title}</span>
        {price && <span className="ml-auto font-heading font-semibold text-electrik">{price}</span>}
      </span>
      {sub && <span className="mt-2 block pl-7 text-xs text-steel leading-relaxed">{sub}</span>}
    </button>
  );
}

export default function Rejoindre() {
  const { user, loading: authLoading } = useAuth();
  const [tarifs, setTarifs] = useState(null);
  const [reinscription, setReinscription] = useState(null);
  const [dossier, setDossier] = useState(undefined);
  const [demandeDone, setDemandeDone] = useState("");
  const [demandeProposee, setDemandeProposee] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const memberId = searchParams.get("licencie") || "";
  const [licencies, setLicencies] = useState([]);
  const [memberIdDefaut, setMemberIdDefaut] = useState("");
  const [packTailles, setPackTailles] = useState({ maillot: "", cuissard: "", veste: "" });
  const [info, setInfo] = useState({
    nom: "", prenom: "", adresse: "", ville: "", code_postal: "", telephone: "", email: "", date_naissance: "",
  });
  const [ffcOption, setFfcOption] = useState("aucune");
  const [fsgt, setFsgt] = useState(false);
  const [flags, setFlags] = useState({ reglement: false, garanties: false, urgence: false, droit_image: false, lu_approuve: false, exactitude: false, conditions: false, reglement_club: false });
  const [submitting, setSubmitting] = useState(false);
  const [docsOblig, setDocsOblig] = useState([]);
  const [docChecks, setDocChecks] = useState({});
  const [docConsultes, setDocConsultes] = useState({});
  const [lecture, setLecture] = useState(null);
  const [ficheSante, setFicheSante] = useState({
    responsable1_nom: "", responsable1_telephone: "", responsable2_nom: "", responsable2_telephone: "",
    urgence_nom: "", urgence_telephone: "", urgence_lien: "",
    probleme_sante: "", allergies: "", traitements: "", precautions: "", infos_urgence: "",
  });
  const [autorisation, setAutorisation] = useState({
    responsable_nom: "", responsable_lien: "", responsable_telephone: "", responsable_email: "",
    activites: false, deplacements: false, prise_en_charge_urgence: false, droit_image: false, engagement_informer: false,
  });

  useEffect(() => {
    api
      .get("/tarifs")
      .then(({ data }) => setTarifs(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    if (user?.role !== "licencie") {
      setDossier(null);
      return;
    }
    const params = memberId ? { member_id: memberId } : {};
    api
      .get("/espace/donnees-reinscription", { params })
      .then(({ data }) => {
        setReinscription(data);
        setInfo({
          nom: data.nom || "", prenom: data.prenom || "", adresse: data.adresse || "",
          ville: data.ville || "", code_postal: data.code_postal || "", telephone: data.telephone || "",
          email: data.email || "", date_naissance: data.date_naissance || "",
        });
      })
      .catch(() => {});
    api.get("/espace/tableau-de-bord", { params }).then(({ data }) => setDossier(data.dossier_2027 || null)).catch(() => setDossier(null));
    api.get("/espace/mes-licencies").then(({ data }) => {
      setLicencies(data.licencies || []);
      setMemberIdDefaut(data.member_id_defaut || "");
    }).catch(() => {});
  }, [user, memberId]);

  useEffect(() => {
    api.get("/documents/inscription/obligatoires").then(({ data }) => setDocsOblig(data)).catch(() => {});
  }, []);

  const ageReg = tarifs && info.date_naissance ? Number(tarifs.saison) - Number(String(info.date_naissance).slice(0, 4)) : null;
  // Règle 2027 : âge précis au 31/12 de la saison (jour, mois, année) → catégorie PROPOSÉE (jamais de tarif avant validation admin)
  const ageAu3112 = (dn) => {
    if (!dn || !tarifs) return null;
    const v = String(dn).slice(0, 10);
    let y, m, d;
    if (v.includes("-")) [y, m, d] = v.split("-").map(Number);
    else [d, m, y] = v.split("/").map(Number);
    if (!y || !m || !d || y < 1900) return null;
    let age = Number(tarifs.saison) - y;
    if (12 < m || (12 === m && 31 < d)) age -= 1;
    return age;
  };
  const ageProp = ageAu3112(info.date_naissance);
  const proposee = ageProp === null ? null : ageProp >= 2 && ageProp <= 16 ? "ecole_de_velo" : "club";
  const mineur = ["baby", "jeune"].includes(ffcOption) || (ageReg !== null && !Number.isNaN(ageReg) && ageReg < 18);
  const docsRequis = docsOblig.filter((d) => d.type === "reglement_interieur" || mineur);
  const docsManquants = !docsOblig.some((d) => d.type === "reglement_interieur") || (mineur && !docsOblig.some((d) => d.type === "fiche_sante"));
  const docsOk = docsRequis.length > 0 && !docsManquants && docsRequis.every((d) => docChecks[d.type]);
  const setFs = (k) => (e) => setFicheSante({ ...ficheSante, [k]: e.target.value });
  const setAuto = (k) => (e) => setAutorisation({ ...autorisation, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const setInfoField = (k) => (e) => setInfo({ ...info, [k]: e.target.value });
  const setFlag = (k) => (e) => setFlags({ ...flags, [k]: e.target.checked });

  const ffcSel = tarifs?.ffc.find((o) => o.id === ffcOption) || null;
  const categorie = dossier?.categorie_2027 || "";
  const isEcole = categorie === "ecole_de_velo";
  const PACK_CENTS = tarifs?.pack_ecole_cents || 27000;
  // RÈGLE 2027 : 34 € = FSGT École de vélo, 38,15 € = FSGT adulte — jamais mélangés (le serveur recalcule toujours)
  const fsgtCents = tarifs ? (isEcole ? tarifs.fsgt_ecole_cents : tarifs.fsgt_cents) : 0;
  const mode = !user
    ? "demande"
    : dossier === undefined
      ? "loading"
      : dossier && (dossier.payment_status === "paid" || dossier.statut === "INSCRIPTION VALIDÉE")
        ? "paye"
        : dossier && ["ecole_de_velo", "club"].includes(categorie)
          ? "parcours"
          : dossier
            ? "attente"
            : "demande";
  const total = !tarifs
    ? 0
    : isEcole && mode === "parcours"
      ? PACK_CENTS + (fsgt ? fsgtCents : 0)
      : tarifs.cotisation_cents + (ffcSel?.prix_cents || 0) + (fsgt ? fsgtCents : 0);
  const today = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric" }).format(new Date());
  // Règle 2027 : date de naissance modifiée → si la catégorie proposée change, nouvelle validation club obligatoire
  const dateInvalideCategorie = mode === "parcours" && Boolean(proposee) && Boolean(categorie) && proposee !== categorie;
  const reglementDoc = docsOblig.find((d) => d.type === "reglement_interieur");

  const submitDemande = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...info, exactitude: flags.exactitude, conditions: flags.conditions, reglement: flags.reglement_club, member_id: memberId || "" };
      const { data } = user
        ? await api.post("/adhesion/reinscription-2027", payload)
        : await api.post("/adhesion/demande", payload);
      setDemandeDone(data.message);
      setDemandeProposee(data.dossier || null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/adhesion/inscription", {
        ...info,
        member_id: memberId || "",
        ffc_option: isEcole ? "pack_ecole" : ffcOption,
        fsgt,
        ...flags,
        reglement: Boolean(docChecks.reglement_interieur),
        documents_valides: docsRequis.filter((d) => docChecks[d.type]).map((d) => ({ type: d.type, version: d.version })),
        fiche_sante: mineur ? ficheSante : null,
        autorisation_parentale: mineur ? autorisation : null,
        pack_tailles: isEcole ? packTailles : null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  if (authLoading || (user && dossier === undefined) || (mode === "parcours" && !tarifs)) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-rouge" size={32} />
      </div>
    );
  }

  const head = (
    <>
      <SEO title="Rejoindre le Club" description="Adhésion en ligne au Club Cycliste Le Boulou — saison 2027 : déposez votre dossier, le club valide votre catégorie, puis vous finalisez depuis votre espace licencié." />
      <PageHeader kicker="Saison 2027 • Adhésion • Licences • Équipements" title="Rejoindre le Club" image={IMAGES.hero} position="center 60%" />
    </>
  );

  if (demandeDone) {
    return (
      <>
        {head}
        <section className="py-16 sm:py-24 bg-mist" data-testid="demande-success">
          <div className="max-w-[700px] mx-auto px-5 sm:px-8">
            <Reveal>
              <div className="bg-white border border-navy/10 p-8 sm:p-10">
                <div className="w-14 h-14 bg-green-100 text-green-700 flex items-center justify-center mb-6">
                  <Check size={24} />
                </div>
                <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl mb-3">Dossier enregistré</h2>
                <p className="text-navy/80 leading-relaxed">{demandeDone}</p>
                {demandeProposee && (
                  <p className="mt-4 border border-navy/15 bg-mist px-4 py-3 text-sm text-navy/80" data-testid="demande-categorie-proposee">
                    {demandeProposee.age_31_12 !== null && demandeProposee.age_31_12 !== undefined && demandeProposee.age_31_12 < 2
                      ? "Cette date de naissance ne correspond pas aux conditions du Pack École de vélo 2027. Le club examinera votre dossier."
                      : <>
                          Âge au 31/12/{demandeProposee.saison || "2027"} : <strong>{demandeProposee.age_31_12} ans</strong> — catégorie
                          proposée au club : <strong className="text-rouge">{demandeProposee.categorie_proposee === "ecole_de_velo" ? "École de vélo" : "Licencié club"}</strong>.
                        </>}
                  </p>
                )}
                {!user && (
                  <p className="mt-4 text-sm text-steel">
                    Un email vient de vous être envoyé pour créer l'accès à votre espace licencié : c'est là que vous
                    finaliserez votre inscription une fois votre catégorie validée par le club.
                  </p>
                )}
                <Link to={user ? "/espace/tableau-de-bord" : "/espace"} data-testid="demande-goto-espace" className="mt-8 inline-flex items-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-navy transition-colors duration-300">
                  Aller à mon espace licencié
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </>
    );
  }

  if (mode === "attente") {
    return (
      <>
        {head}
        <section className="py-16 sm:py-24 bg-mist" data-testid="attente-categorie">
          <div className="max-w-[700px] mx-auto px-5 sm:px-8">
            <Reveal>
              <div className="bg-white border border-navy/10 p-8 sm:p-10">
                <span className="inline-block text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 bg-amber-100 text-amber-700 mb-5" data-testid="attente-statut">
                  {dossier.statut}
                </span>
                <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl mb-3">Dossier en cours de vérification</h2>
                <p className="text-navy/80 leading-relaxed">
                  Votre dossier est en cours de vérification par le Club Cycliste Le Boulou. Vous pourrez poursuivre
                  votre inscription dès que votre catégorie aura été validée — vous recevrez un email.
                </p>
                {dossier.categorie_proposee && (
                  <p className="mt-4 border border-navy/15 bg-white px-4 py-3 text-sm text-navy/75" data-testid="attente-categorie-proposee">
                    Catégorie proposée au club :{" "}
                    <strong className="text-rouge">{dossier.categorie_proposee === "ecole_de_velo" ? "École de vélo" : "Licencié club"}</strong>
                    {dossier.age_31_12 !== null && dossier.age_31_12 !== undefined && <> — {dossier.age_31_12} ans au 31/12/{dossier.saison || "2027"}</>}.
                  </p>
                )}
                <Link to="/espace/tableau-de-bord" data-testid="attente-goto-dashboard" className="mt-8 inline-flex items-center gap-3 border border-navy/25 text-navy font-heading uppercase tracking-wider text-sm px-8 py-4 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
                  Retour à mon tableau de bord
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </>
    );
  }

  if (mode === "paye") {
    return (
      <>
        {head}
        <section className="py-16 sm:py-24 bg-mist" data-testid="inscription-deja-payee">
          <div className="max-w-[700px] mx-auto px-5 sm:px-8">
            <Reveal>
              <div className="bg-white border border-navy/10 p-8 sm:p-10">
                <div className="w-14 h-14 bg-green-100 text-green-700 flex items-center justify-center mb-6">
                  <Check size={24} />
                </div>
                <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl mb-3">Inscription {tarifs?.saison || "2027"} enregistrée</h2>
                <p className="text-navy/80 leading-relaxed">
                  Votre paiement a été reçu{dossier?.total_label ? ` (${dossier.total_label})` : ""} et votre dossier est entre les mains du club.
                  Suivez son avancement depuis votre espace licencié.
                </p>
                <Link to="/espace/tableau-de-bord" data-testid="paye-goto-dashboard" className="mt-8 inline-flex items-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-navy transition-colors duration-300">
                  Mon tableau de bord
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </>
    );
  }

  if (mode === "demande") {
    return (
      <>
        {head}
        <section className="py-14 border-b border-navy/10">
          <div className="max-w-[1000px] mx-auto px-5 sm:px-8">
            <Reveal>
              <div className="space-y-4 text-navy/80 text-base sm:text-lg leading-relaxed max-w-3xl">
                <p>Bienvenue au Club Cycliste Le Boulou.</p>
                <p>
                  Déposez votre demande d'inscription ci-dessous. Le club vérifie votre dossier et valide votre
                  catégorie (École de vélo ou Licencié club) : vous recevrez alors un email pour finaliser votre
                  inscription depuis votre espace licencié — choix de la licence, équipements, documents à valider
                  puis paiement sécurisé.
                </p>
              </div>
              <div className="mt-8 flex gap-2 overflow-x-auto pb-2" data-testid="inscription-steps">
                {["Mes informations", "Validation par le club", "Documents & paiement", "Confirmation"].map((s, i) => (
                  <span key={s} className="shrink-0 flex items-center gap-2 border border-navy/15 px-3.5 py-2 text-xs font-heading uppercase tracking-wider text-navy/70">
                    <span className="flex h-5 w-5 items-center justify-center bg-navy text-white text-[10px] font-semibold">{i + 1}</span>
                    {s}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <form onSubmit={submitDemande} className="pb-24" data-testid="demande-form">
          <div className="max-w-[1000px] mx-auto px-5 sm:px-8">
            <section className="py-14" data-testid="step-infos">
              <StepHead num="1" title="Mes informations" />
              {reinscription && (
                <div className="mb-8 bg-navy text-white p-5 sm:p-6" data-testid="reinscription-prefill-note">
                  <p className="font-heading uppercase tracking-wider text-sm text-rouge">Réinscription {tarifs?.saison || "2027"}</p>
                  <p className="mt-1.5 text-sm text-white/75">
                    Vos informations 2026 ont été pré-remplies à partir de votre dossier licencié. Corrigez simplement
                    ce qui a changé — votre dossier existant sera mis à jour, sans créer de doublon.
                  </p>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="adh-nom" className={labelCls}>Nom *</label>
                  <input id="adh-nom" data-testid="adh-nom" required value={info.nom} onChange={setInfoField("nom")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="adh-prenom" className={labelCls}>Prénom *</label>
                  <input id="adh-prenom" data-testid="adh-prenom" required value={info.prenom} onChange={setInfoField("prenom")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="adh-naissance" className={labelCls}>Date de naissance *</label>
                  <input id="adh-naissance" data-testid="adh-naissance" required type="date" value={info.date_naissance} onChange={setInfoField("date_naissance")} className={inputCls} />
                </div>
                {proposee && (
                  <div className="self-end" data-testid="categorie-proposee-note">
                    {ageProp !== null && ageProp < 2 ? (
                      <p className="border border-amber-300 bg-amber-50 text-amber-900 text-xs leading-relaxed px-4 py-3">
                        Cette date de naissance ne correspond pas aux conditions du Pack École de vélo 2027. Le club examinera votre dossier.
                      </p>
                    ) : (
                      <p className="border border-navy/15 bg-white text-navy/75 text-xs leading-relaxed px-4 py-3">
                        Âge au 31/12/{tarifs.saison} : <strong>{ageProp} ans</strong> — catégorie proposée :{" "}
                        <strong className="text-rouge">{proposee === "ecole_de_velo" ? "École de vélo" : "Licencié club"}</strong>.
                        Le club confirme chaque catégorie avant le paiement.
                      </p>
                    )}
                  </div>
                )}
                <div className="sm:col-span-2">
                  <label htmlFor="adh-adresse" className={labelCls}>Adresse *</label>
                  <input id="adh-adresse" data-testid="adh-adresse" required value={info.adresse} onChange={setInfoField("adresse")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="adh-cp" className={labelCls}>Code postal *</label>
                  <input id="adh-cp" data-testid="adh-code-postal" required value={info.code_postal} onChange={setInfoField("code_postal")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="adh-ville" className={labelCls}>Ville *</label>
                  <input id="adh-ville" data-testid="adh-ville" required value={info.ville} onChange={setInfoField("ville")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="adh-tel" className={labelCls}>Téléphone *</label>
                  <input id="adh-tel" data-testid="adh-telephone" required type="tel" value={info.telephone} onChange={setInfoField("telephone")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="adh-email" className={labelCls}>Email *</label>
                  <input id="adh-email" data-testid="adh-email" required type="email" value={info.email} onChange={setInfoField("email")} className={inputCls} readOnly={Boolean(user)} />
                  {user && <p className="mt-1.5 text-xs text-steel">Identifiant de votre compte licencié.</p>}
                </div>
              </div>
              <div className="mt-8 space-y-4">
                {[
                  { key: "exactitude", testId: "check-exactitude", text: "Je certifie l'exactitude des informations renseignées." },
                  { key: "conditions", testId: "check-conditions", text: "J'accepte les conditions d'adhésion du Club Cycliste Le Boulou." },
                ].map((c) => (
                  <label key={c.key} className="flex items-start gap-3 text-sm text-navy/80 cursor-pointer bg-white border border-navy/10 p-4 hover:border-rouge/50 transition-colors duration-300" data-testid={`${c.testId}-label`}>
                    <input
                      type="checkbox"
                      required
                      checked={flags[c.key]}
                      onChange={(e) => setFlags({ ...flags, [c.key]: e.target.checked })}
                      data-testid={c.testId}
                      className="mt-1 h-4 w-4 shrink-0 accent-[#D90429]"
                    />
                    {c.text}
                  </label>
                ))}
                <div className="bg-white border border-navy/10 p-4 hover:border-rouge/50 transition-colors duration-300" data-testid="check-reglement-label">
                  <label className="flex items-start gap-3 text-sm text-navy/80 cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      checked={flags.reglement_club}
                      onChange={(e) => setFlags({ ...flags, reglement_club: e.target.checked })}
                      data-testid="check-reglement"
                      className="mt-1 h-4 w-4 shrink-0 accent-[#D90429]"
                    />
                    <span>
                      J'ai lu le <strong>règlement intérieur</strong> du Club Cycliste Le Boulou et je l'accepte
                      {reglementDoc?.version ? ` (version ${reglementDoc.version})` : ""}. *
                    </span>
                  </label>
                  {reglementDoc && (
                    <button
                      type="button"
                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/inscription/${reglementDoc.id}/lire`, "_blank", "noopener")}
                      data-testid="lire-reglement-btn"
                      className="mt-3 ml-7 inline-flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-rouge border border-rouge/40 px-4 py-2 hover:bg-rouge hover:text-white transition-[background-color,color] duration-300"
                    >
                      Lire le règlement intérieur
                    </button>
                  )}
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="demande-submit"
                className="mt-8 inline-flex items-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-9 py-5 hover:bg-navy disabled:opacity-60 transition-colors duration-300"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                Envoyer ma demande d'inscription
              </button>
              <p className="mt-3 text-[11px] text-steel">
                Aucun paiement à cette étape : le club valide d'abord votre catégorie, puis vous finalisez votre
                inscription depuis votre espace licencié.
              </p>
            </section>
          </div>
        </form>
      </>
    );
  }


  return (
    <>
      <SEO
        title="Rejoindre le Club"
        description={`Adhésion en ligne au Club Cycliste Le Boulou — saison ${tarifs.saison} : cotisation club, licences FFC et FSGT, équipements du club, paiement sécurisé.`}
      />
      <PageHeader kicker={`Saison ${tarifs.saison} • Adhésion • Licences • Équipements`} title="Rejoindre le Club" image={IMAGES.hero} position="center 60%" />

      <section className="py-14 border-b border-navy/10">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="space-y-4 text-navy/80 text-base sm:text-lg leading-relaxed max-w-3xl">
              <p>Bienvenue au Club Cycliste Le Boulou.</p>
              <p>
                Pour rejoindre le club, la cotisation annuelle est obligatoire. Vous pouvez ensuite choisir la licence
                correspondant à votre pratique : une licence FFC et/ou une licence FSGT, ou aucune licence.
              </p>
              <p>Vous pouvez également commander vos équipements directement lors de votre inscription.</p>
            </div>
            <div className="mt-8 flex gap-2 overflow-x-auto pb-2" data-testid="inscription-steps">
              {STEPS.map((s, i) => (
                <span key={s} className="shrink-0 flex items-center gap-2 border border-navy/15 px-3.5 py-2 text-xs font-heading uppercase tracking-wider text-navy/70">
                  <span className="flex h-5 w-5 items-center justify-center bg-navy text-white text-[10px] font-semibold">{i + 1}</span>
                  {s}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {mode === "parcours" && licencies.length > 0 && (
        <section className="pt-10">
          <div className="max-w-[1000px] mx-auto px-5 sm:px-8">
            <p className="font-heading uppercase tracking-[0.25em] text-rouge text-xs mb-3">Mes licenciés</p>
            <div className="flex flex-wrap gap-2" data-testid="rejoindre-mes-licencies">
              {licencies.map((l) => {
                const selected = (memberId || memberIdDefaut) === l.member_id;
                return (
                  <button
                    key={l.member_id}
                    type="button"
                    data-testid={`rejoindre-licencie-${l.member_id.slice(0, 8)}`}
                    onClick={() => setSearchParams(l.member_id === memberIdDefaut ? {} : { licencie: l.member_id })}
                    className={`px-5 py-3 text-xs font-heading uppercase tracking-wider border transition-[border-color,background-color,color] duration-300 ${
                      selected ? "bg-navy text-white border-navy" : "bg-white text-navy/70 border-navy/20 hover:border-rouge hover:text-rouge"
                    }`}
                  >
                    {l.prenom} {l.nom}
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-steel">Chaque licencié possède son propre dossier : documents, équipements et paiement restent indépendants.</p>
          </div>
        </section>
      )}

      <form id="adhesion-form" onSubmit={submit} className="pb-40">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8">
          <section className="py-14 border-b border-navy/10" data-testid="step-infos">
            <StepHead num="1" title="Mes informations" />
            {reinscription && (
              <div className="mb-8 bg-navy text-white p-5 sm:p-6" data-testid="reinscription-prefill-note">
                <p className="font-heading uppercase tracking-wider text-sm text-rouge">Réinscription 2027</p>
                <p className="mt-1.5 text-sm text-white/75">
                  Vos informations 2026 ont été pré-remplies à partir de votre dossier licencié
                  {reinscription.numero_licence ? ` (licence n° ${reinscription.numero_licence})` : ""}.
                  Corrigez simplement ce qui a changé — votre dossier existant sera mis à jour, sans créer de doublon.
                </p>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="adh-nom" className={labelCls}>Nom *</label>
                <input id="adh-nom" data-testid="adh-nom" required value={info.nom} onChange={setInfoField("nom")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="adh-prenom" className={labelCls}>Prénom *</label>
                <input id="adh-prenom" data-testid="adh-prenom" required value={info.prenom} onChange={setInfoField("prenom")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="adh-naissance" className={labelCls}>Date de naissance *</label>
                <input id="adh-naissance" data-testid="adh-naissance" required type="date" value={info.date_naissance} onChange={setInfoField("date_naissance")} className={inputCls} />
              </div>
              {dateInvalideCategorie && (
                <div className="sm:col-span-2 border border-amber-300 bg-amber-50 text-amber-900 text-sm leading-relaxed px-4 py-3" data-testid="date-modifiee-warning">
                  Vous avez modifié votre date de naissance : votre catégorie ({proposee === "ecole_de_velo" ? "École de vélo" : "Licencié club"} désormais proposée)
                  devra être validée à nouveau par le club avant tout paiement.
                </div>
              )}
              <div className="sm:col-span-2">
                <label htmlFor="adh-adresse" className={labelCls}>Adresse *</label>
                <input id="adh-adresse" data-testid="adh-adresse" required value={info.adresse} onChange={setInfoField("adresse")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="adh-cp" className={labelCls}>Code postal *</label>
                <input id="adh-cp" data-testid="adh-code-postal" required value={info.code_postal} onChange={setInfoField("code_postal")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="adh-ville" className={labelCls}>Ville *</label>
                <input id="adh-ville" data-testid="adh-ville" required value={info.ville} onChange={setInfoField("ville")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="adh-tel" className={labelCls}>Téléphone *</label>
                <input id="adh-tel" data-testid="adh-telephone" required type="tel" value={info.telephone} onChange={setInfoField("telephone")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="adh-email" className={labelCls}>Email *</label>
                <input id="adh-email" data-testid="adh-email" required type="email" value={info.email} onChange={setInfoField("email")} className={inputCls} />
                {reinscription && <p className="mt-1.5 text-xs text-steel">Votre email est votre identifiant de connexion — si vous le modifiez, il deviendra votre nouvel identifiant.</p>}
              </div>
            </div>
          </section>

          <section className="py-14 border-b border-navy/10" data-testid="step-adhesion">
            <StepHead num="2" title={isEcole ? "Mon pack École de vélo" : "Mon adhésion"}>
              {isEcole ? (
                <p>Votre catégorie École de vélo a été validée par le club : votre inscription comprend le pack annuel tout compris.</p>
              ) : (
                <p>La cotisation au Club Cycliste Le Boulou est obligatoire pour toute personne qui adhère au club.</p>
              )}
            </StepHead>
            {isEcole ? (
              <div className="bg-navy text-white p-6 sm:p-8" data-testid="pack-ecole">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <span className="flex h-6 w-6 items-center justify-center bg-rouge" title="Pack validé par le club">
                      <Check size={15} />
                    </span>
                    <div>
                      <p className="font-heading uppercase font-semibold tracking-tight text-xl">Pack École de vélo {tarifs.saison}</p>
                      <p className="text-white/60 text-xs uppercase tracking-widest mt-1">Adhésion + licence FFC + maillot + cuissard + veste</p>
                    </div>
                  </div>
                  <p className="font-heading font-semibold text-3xl" data-testid="pack-prix">{eur(PACK_CENTS)} / an</p>
                </div>
                <ul className="mt-6 pt-6 border-t border-white/15 grid sm:grid-cols-5 gap-2 text-xs uppercase tracking-wider text-white/80" data-testid="pack-inclus">
                  {["Adhésion club", "Licence FFC", "1 maillot", "1 cuissard", "1 veste"].map((x) => (
                    <li key={x} className="flex items-center gap-2"><Check size={12} className="text-rouge shrink-0" />{x}</li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-white/15 grid sm:grid-cols-3 gap-4">
                  {["maillot", "cuissard", "veste"].map((item) => (
                    <div key={item}>
                      <label className="block font-heading uppercase tracking-wider text-xs text-white/70 mb-1.5" htmlFor={`pack-${item}`}>Taille {item} *</label>
                      <select
                        id={`pack-${item}`}
                        data-testid={`pack-taille-${item}`}
                        required
                        value={packTailles[item]}
                        onChange={(e) => setPackTailles({ ...packTailles, [item]: e.target.value })}
                        className="w-full border border-white/25 bg-navy text-white px-3 py-2.5 text-sm focus:outline-none focus:border-rouge"
                      >
                        <option value="">— Choisir —</option>
                        {TAILLES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-4 bg-navy text-white p-6 sm:p-8">
                <div className="flex items-center gap-4">
                  <span className="flex h-6 w-6 items-center justify-center bg-rouge" title="Cotisation obligatoire">
                    <Check size={15} />
                  </span>
                  <div>
                    <p className="font-heading uppercase font-semibold tracking-tight text-xl">Cotisation club</p>
                    <p className="text-white/60 text-xs uppercase tracking-widest mt-1">Saison {tarifs.saison} — obligatoire</p>
                  </div>
                </div>
                <p className="font-heading font-semibold text-3xl" data-testid="cotisation-prix">{eur(tarifs.cotisation_cents)}</p>
              </div>
            )}
          </section>

          <section className="py-14 border-b border-navy/10" data-testid="step-licence">
            <StepHead num="3" title="Ma licence">
              {isEcole ? (
                <p>La licence FFC est incluse dans votre pack École de vélo. Vous pouvez ajouter une licence FSGT en complément si vous le souhaitez.</p>
              ) : (
                <>
                  <p>
                    La licence n'est pas obligatoire pour adhérer au club. Choisissez au maximum une licence FFC, et ajoutez
                    si vous le souhaitez une licence FSGT en complément.
                  </p>
                  <p className="mt-3">
                    La licence est recommandée car elle offre une assurance adaptée à la pratique du cyclisme selon le type
                    de licence.
                  </p>
                </>
              )}
            </StepHead>

            {!isEcole && (
              <>
                <p className="font-heading uppercase tracking-wider text-sm text-rouge mb-4">Licence FFC — saison {tarifs.saison} (un seul choix)</p>
                <div className="grid sm:grid-cols-2 gap-4" data-testid="ffc-options">
                  {tarifs.ffc.map((o) => (
                    <RadioCard
                      key={o.id}
                      checked={ffcOption === o.id}
                      onSelect={() => setFfcOption(o.id)}
                      title={o.label}
                      price={eur(o.prix_cents)}
                      testId={`ffc-${o.id}`}
                    />
                  ))}
                  <RadioCard
                    checked={ffcOption === "aucune"}
                    onSelect={() => setFfcOption("aucune")}
                    title="Je ne souhaite pas prendre de licence FFC"
                    sub="Adhésion au club sans licence FFC."
                    testId="ffc-aucune"
                  />
                </div>
              </>
            )}

            <div className="mt-10">
              <p className="font-heading uppercase tracking-wider text-sm text-rouge mb-4">
                {isEcole ? `Option — Licence FSGT ${tarifs.saison}` : `Licence FSGT ${tarifs.saison} — option indépendante`}
              </p>
              <label
                data-testid="licence-fsgt-label"
                className={`flex items-center gap-4 border p-5 cursor-pointer transition-[border-color,background-color] duration-300 ${
                  fsgt ? "border-rouge bg-rouge/5" : "border-navy/15 bg-white hover:border-rouge/50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={fsgt}
                  onChange={(e) => setFsgt(e.target.checked)}
                  data-testid="licence-fsgt"
                  className="h-4 w-4 shrink-0 accent-[#D90429]"
                />
                <span className="flex-1">
                  <span className="font-heading uppercase font-semibold tracking-tight text-navy">
                    {isEcole ? `Je souhaite également prendre la licence FSGT ${tarifs.saison} (+${eur(fsgtCents)})` : `Licence FSGT ${tarifs.saison}`}
                  </span>
                  <span className="block text-xs text-steel mt-1">
                    {isEcole
                      ? "Option facultative — tarif École de vélo. La licence FSGT est enregistrée sur le dossier du jeune licencié."
                      : "Fédération Sportive et Gymnique du Travail — peut compléter une licence FFC. Tarif adulte."}
                  </span>
                </span>
                <span className="font-heading font-semibold text-electrik" data-testid="licence-fsgt-prix">{isEcole ? `+${eur(fsgtCents)}` : eur(fsgtCents)}</span>
              </label>
            </div>
          </section>

          <section className="py-14 border-b border-navy/10" data-testid="step-equipements">
            <StepHead num="4" title="Mon équipement" />
            <div className="bg-mist border border-navy/10 p-7 sm:p-9 flex flex-wrap items-center justify-between gap-6">
              <div className="max-w-xl">
                <p className="text-navy/80 text-sm sm:text-base leading-relaxed">
                  Les équipements et tenues du club (maillots, cuissards, vestes…) ne sont plus vendus sur ce site.
                  Ils sont disponibles sur la boutique partenaire officielle du club.
                </p>
                <p className="mt-3 text-xs text-steel">
                  Le règlement de vos commandes d'équipements s'effectue directement auprès de la boutique partenaire.
                </p>
              </div>
              <a
                href="https://www.expressoshop.fr/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="boutique-equipements-btn"
                className="group inline-flex items-center gap-3 bg-navy text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-rouge transition-colors duration-300"
              >
                Boutique équipements
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </a>
            </div>
          </section>

          <section id="adhesion-recap" className="py-14 scroll-mt-28" data-testid="step-recap">
            <StepHead num="5" title="Récapitulatif" />
            <div className="bg-navy text-white p-7 sm:p-10" data-testid="recap-box">
              <p className="font-heading uppercase tracking-wider text-xs text-rouge mb-6">Récapitulatif de votre inscription — saison {tarifs.saison}</p>
              <div className="space-y-3 text-sm">
                {isEcole ? (
                  <p className="flex justify-between gap-4" data-testid="recap-pack">
                    <span className="text-white/70">Pack École de vélo {tarifs.saison} <span className="text-white/45">— adhésion club + licence FFC + maillot ({packTailles.maillot || "…"}) + cuissard ({packTailles.cuissard || "…"}) + veste ({packTailles.veste || "…"})</span></span>
                    <span className="font-semibold">{eur(PACK_CENTS)}</span>
                  </p>
                ) : (
                  <p className="flex justify-between gap-4"><span className="text-white/70">Cotisation club</span><span className="font-semibold">{eur(tarifs.cotisation_cents)}</span></p>
                )}
                {!isEcole && ffcSel && (
                  <p className="flex justify-between gap-4">
                    <span className="text-white/70">Licence {ffcSel.label}</span>
                    <span className="font-semibold">{eur(ffcSel.prix_cents)}</span>
                  </p>
                )}
                {fsgt && (
                  <p className="flex justify-between gap-4" data-testid="recap-fsgt"><span className="text-white/70">Licence FSGT {tarifs.saison}</span><span className="font-semibold">{eur(fsgtCents)}</span></p>
                )}
              </div>
              <div className="mt-6 pt-6 border-t border-white/15 flex flex-wrap items-end justify-between gap-3">
                <span className="font-heading uppercase tracking-wider text-white/70 text-sm">Total à payer</span>
                <span className="font-heading font-semibold text-4xl sm:text-5xl text-white" data-testid="recap-total">{eur(total)}</span>
              </div>
            </div>
          </section>

          <section className="py-14 border-b border-navy/10" data-testid="step-documents">
            <StepHead num="6" title="Documents à lire et à valider">
              <p>
                Avant de procéder au paiement, vous devez prendre connaissance des documents applicables à votre
                inscription et confirmer leur lecture.
              </p>
              {mineur && (
                <p className="mt-3 font-semibold text-navy" data-testid="documents-mineur-note">
                  Licencié mineur : la fiche santé et l'autorisation parentale sont également obligatoires et doivent
                  être complétées par le représentant légal.
                </p>
              )}
            </StepHead>

            {docsManquants ? (
              <div className="bg-amber-50 border border-amber-300 text-amber-800 text-sm px-5 py-4" data-testid="documents-indisponibles">
                Les documents obligatoires sont en cours de publication par le club. Merci de réessayer ultérieurement.
              </div>
            ) : (
              <div className="space-y-4" data-testid="documents-list">
                {docsRequis.map((doc) => (
                  <div key={doc.id} className="bg-white border border-navy/10 p-5 sm:p-6" data-testid={`doc-card-${doc.type}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex items-start gap-4 min-w-0">
                        <span className="w-11 h-11 shrink-0 bg-navy/5 text-rouge flex items-center justify-center">
                          <FileText size={20} />
                        </span>
                        <div className="min-w-0">
                          <p className="font-heading uppercase font-semibold tracking-tight text-navy">{doc.title}</p>
                          <p className="text-xs text-steel mt-1">
                            Document officiel du club{doc.version ? ` · Version ${doc.version}` : ""}{doc.date_effet ? ` · en vigueur le ${fmtDateFr(doc.date_effet)}` : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {docConsultes[doc.type] && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-green-700 bg-green-100 px-2.5 py-1" data-testid={`doc-consulte-${doc.type}`}>Consulté</span>
                        )}
                        <button
                          type="button"
                          onClick={() => { setLecture(doc); setDocConsultes({ ...docConsultes, [doc.type]: true }); }}
                          data-testid={`doc-lire-${doc.type}`}
                          className="inline-flex items-center gap-2 border border-navy/25 text-navy font-heading uppercase tracking-wider text-xs px-5 py-2.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
                        >
                          <BookOpen size={14} /> Lire le document
                        </button>
                      </div>
                    </div>
                    <label className="mt-4 flex items-start gap-3 text-sm text-navy/80 cursor-pointer bg-mist border border-navy/10 p-4 hover:border-rouge/50 transition-colors duration-300" data-testid={`doc-check-${doc.type}-label`}>
                      <input
                        type="checkbox"
                        required
                        checked={Boolean(docChecks[doc.type])}
                        onChange={(e) => setDocChecks({ ...docChecks, [doc.type]: e.target.checked })}
                        data-testid={`doc-check-${doc.type}`}
                        className="mt-1 h-4 w-4 shrink-0 accent-[#D90429]"
                      />
                      {DOC_CHECK_TEXTS[doc.type]}
                    </label>

                    {doc.type === "fiche_sante" && mineur && (
                      <div className="mt-5 border-t border-navy/10 pt-5" data-testid="fiche-sante-form">
                        <p className="font-heading uppercase tracking-wider text-xs text-steel mb-4">Fiche santé — à compléter par le représentant légal</p>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <label className={labelCls} htmlFor="fs-resp1">Responsable légal 1 — Nom et prénom *</label>
                            <input id="fs-resp1" data-testid="fs-resp1-nom" required value={ficheSante.responsable1_nom} onChange={setFs("responsable1_nom")} className={inputCls} />
                          </div>
                          <div>
                            <label className={labelCls} htmlFor="fs-resp1-tel">Téléphone *</label>
                            <input id="fs-resp1-tel" data-testid="fs-resp1-tel" required value={ficheSante.responsable1_telephone} onChange={setFs("responsable1_telephone")} className={inputCls} />
                          </div>
                          <div>
                            <label className={labelCls} htmlFor="fs-resp2">Responsable légal 2 — Nom et prénom</label>
                            <input id="fs-resp2" data-testid="fs-resp2-nom" value={ficheSante.responsable2_nom} onChange={setFs("responsable2_nom")} className={inputCls} />
                          </div>
                          <div>
                            <label className={labelCls} htmlFor="fs-resp2-tel">Téléphone</label>
                            <input id="fs-resp2-tel" data-testid="fs-resp2-tel" value={ficheSante.responsable2_telephone} onChange={setFs("responsable2_telephone")} className={inputCls} />
                          </div>
                          <div>
                            <label className={labelCls} htmlFor="fs-urg-nom">Contact d'urgence — Nom et prénom *</label>
                            <input id="fs-urg-nom" data-testid="fs-urgence-nom" required value={ficheSante.urgence_nom} onChange={setFs("urgence_nom")} className={inputCls} />
                          </div>
                          <div>
                            <label className={labelCls} htmlFor="fs-urg-tel">Téléphone d'urgence *</label>
                            <input id="fs-urg-tel" data-testid="fs-urgence-tel" required value={ficheSante.urgence_telephone} onChange={setFs("urgence_telephone")} className={inputCls} />
                          </div>
                          <div className="sm:col-span-2">
                            <label className={labelCls} htmlFor="fs-urg-lien">Lien avec l'enfant</label>
                            <input id="fs-urg-lien" data-testid="fs-urgence-lien" value={ficheSante.urgence_lien} onChange={setFs("urgence_lien")} className={inputCls} />
                          </div>
                          <div className="sm:col-span-2">
                            <label className={labelCls} htmlFor="fs-sante">Problèmes de santé importants à signaler</label>
                            <textarea id="fs-sante" data-testid="fs-probleme-sante" rows={2} value={ficheSante.probleme_sante} onChange={setFs("probleme_sante")} className={inputCls} placeholder="Aucun élément particulier, ou précisez" />
                          </div>
                          <div className="sm:col-span-2">
                            <label className={labelCls} htmlFor="fs-allergies">Allergies et intolérances</label>
                            <textarea id="fs-allergies" data-testid="fs-allergies" rows={2} value={ficheSante.allergies} onChange={setFs("allergies")} className={inputCls} placeholder="Aucune, ou précisez l'allergène et la conduite à tenir" />
                          </div>
                          <div className="sm:col-span-2">
                            <label className={labelCls} htmlFor="fs-traitements">Traitements ou médicaments</label>
                            <textarea id="fs-traitements" data-testid="fs-traitements" rows={2} value={ficheSante.traitements} onChange={setFs("traitements")} className={inputCls} placeholder="Aucun, ou précisez" />
                          </div>
                          <div className="sm:col-span-2">
                            <label className={labelCls} htmlFor="fs-precautions">Précautions particulières</label>
                            <textarea id="fs-precautions" data-testid="fs-precautions" rows={2} value={ficheSante.precautions} onChange={setFs("precautions")} className={inputCls} />
                          </div>
                          <div className="sm:col-span-2">
                            <label className={labelCls} htmlFor="fs-urgence">Informations utiles en cas d'urgence</label>
                            <textarea id="fs-urgence" data-testid="fs-infos-urgence" rows={2} value={ficheSante.infos_urgence} onChange={setFs("infos_urgence")} className={inputCls} />
                          </div>
                        </div>
                      </div>
                    )}

                    {doc.type === "autorisation_parentale" && mineur && (
                      <div className="mt-5 border-t border-navy/10 pt-5" data-testid="autorisation-form">
                        <p className="font-heading uppercase tracking-wider text-xs text-steel mb-4">Autorisation parentale — à compléter par le représentant légal</p>
                        <div className="grid sm:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className={labelCls} htmlFor="ap-resp">Représentant légal — Nom et prénom *</label>
                            <input id="ap-resp" data-testid="ap-responsable-nom" required value={autorisation.responsable_nom} onChange={setAuto("responsable_nom")} className={inputCls} />
                          </div>
                          <div>
                            <label className={labelCls} htmlFor="ap-lien">Lien avec l'enfant</label>
                            <input id="ap-lien" data-testid="ap-responsable-lien" value={autorisation.responsable_lien} onChange={setAuto("responsable_lien")} className={inputCls} placeholder="Père, mère, tuteur…" />
                          </div>
                          <div>
                            <label className={labelCls} htmlFor="ap-tel">Téléphone du représentant légal *</label>
                            <input id="ap-tel" data-testid="ap-responsable-tel" required value={autorisation.responsable_telephone} onChange={setAuto("responsable_telephone")} className={inputCls} />
                          </div>
                          <div>
                            <label className={labelCls} htmlFor="ap-email">E-mail du représentant légal *</label>
                            <input id="ap-email" data-testid="ap-responsable-email" type="email" required value={autorisation.responsable_email} onChange={setAuto("responsable_email")} className={inputCls} />
                          </div>
                        </div>
                        <div className="space-y-3">
                          {[
                            ["activites", "J'autorise mon enfant à participer aux activités du Club Cycliste Le Boulou. *"],
                            ["deplacements", "J'autorise les déplacements organisés par le club dans le cadre des activités."],
                            ["prise_en_charge_urgence", "J'autorise la prise en charge de mon enfant en cas d'urgence. *"],
                            ["droit_image", "J'autorise l'utilisation de l'image de mon enfant dans le cadre de la promotion du club."],
                            ["engagement_informer", "Je m'engage à informer le club de toute modification importante concernant mon enfant. *"],
                          ].map(([k, text]) => (
                            <label key={k} className="flex items-start gap-3 text-sm text-navy/80 cursor-pointer" data-testid={`ap-${k}-label`}>
                              <input type="checkbox" checked={autorisation[k]} onChange={setAuto(k)} data-testid={`ap-${k}`} className="mt-1 h-4 w-4 shrink-0 accent-[#D90429]" />
                              {text}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="py-14 scroll-mt-28" data-testid="step-validation">
            <StepHead num="7" title="Validation et paiement" />
            <div className="space-y-4" data-testid="adhesion-autorisations">
              {[
                { key: "exactitude", testId: "check-exactitude", text: "Je certifie l'exactitude des informations renseignées." },
                { key: "conditions", testId: "check-conditions", text: "J'accepte les conditions d'adhésion du Club Cycliste Le Boulou." },
                { key: "garanties", testId: "check-garanties", text: "Je certifie avoir pris connaissance des garanties minimum accordées par ma licence et de l'intérêt de souscrire aux garanties complémentaires « dommages corporels » qui me sont proposées." },
                { key: "urgence", testId: "check-urgence", text: "J'autorise la direction à faire appel en cas d'urgence aux services de secours et à me prendre en charge en cas de problème de santé." },
                { key: "droit_image", testId: "check-image", text: "J'autorise le Club Cycliste Le Boulou à utiliser les images (photos ou vidéos) prises lors des entraînements et compétitions dans le cadre de la promotion future du club." },
                { key: "lu_approuve", testId: "check-lu-approuve", text: "J'ai lu et approuvé les informations et conditions de mon inscription." },
              ].map((c) => (
                <label key={c.key} className="flex items-start gap-3 text-sm text-navy/80 cursor-pointer bg-white border border-navy/10 p-4 hover:border-rouge/50 transition-colors duration-300" data-testid={`${c.testId}-label`}>
                  <input type="checkbox" required checked={flags[c.key]} onChange={setFlag(c.key)} data-testid={c.testId} className="mt-1 h-4 w-4 shrink-0 accent-[#D90429]" />
                  {c.text}
                </label>
              ))}
            </div>

            <div className="mt-8 border border-navy/15 bg-white p-6" data-testid="signature-block">
              <p className="font-heading uppercase tracking-wider text-xs text-steel mb-4">Signature électronique</p>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <p className="text-navy/80"><span className="text-steel">Nom et prénom :</span> <strong>{info.prenom || "…"} {info.nom || "…"}</strong></p>
                <p className="text-navy/80"><span className="text-steel">Date :</span> <strong>{today}</strong></p>
              </div>
              <p className="mt-3 text-xs text-steel">Lu et approuvé — la validation de ce formulaire et le paiement valent signature électronique.</p>
            </div>

            <div className="mt-8 border-2 border-rouge bg-rouge/5 p-6" data-testid="payment-warning">
              <p className="text-navy font-semibold leading-relaxed">
                Le paiement de la totalité du montant indiqué ci-dessus est nécessaire pour valider définitivement
                votre inscription au Club Cycliste Le Boulou.
              </p>
            </div>

            <div className="mt-8">
              <p className="font-heading uppercase tracking-wider text-xs text-steel mb-4 flex items-center gap-2">
                <ShieldCheck size={14} className="text-rouge" /> Paiement sécurisé par Stripe
              </p>
              <button
                type="submit"
                disabled={submitting || !docsOk || dateInvalideCategorie}
                data-testid="adhesion-submit"
                className="inline-flex items-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-9 py-5 hover:bg-navy disabled:opacity-60 transition-colors duration-300"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                Payer mon inscription {tarifs.saison} — {eur(total)}
              </button>
              {dateInvalideCategorie && (
                <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-300 px-4 py-3" data-testid="payment-revalidation-note">
                  Paiement suspendu : la date de naissance a été modifiée — une nouvelle validation de votre catégorie par le club est nécessaire.
                </p>
              )}
              {!docsOk && (
                <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-300 px-4 py-3" data-testid="payment-blocked-note">
                  Veuillez prendre connaissance et valider tous les documents obligatoires avant de poursuivre vers le paiement.
                </p>
              )}
              <p className="mt-3 text-[11px] text-steel">
                Paiement sécurisé par Stripe. Aucune donnée bancaire n'est stockée par le club. Si le paiement est
                interrompu, vous pourrez le reprendre sans créer de doublon.
              </p>
            </div>
          </section>
        </div>
      </form>

      <div className="fixed bottom-0 inset-x-0 z-40 bg-navy text-white border-t border-white/10" data-testid="sticky-total">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between gap-4">
          <p className="font-heading uppercase tracking-wider text-xs text-white/60">
            Total à payer{" "}
            <span className="block sm:inline font-heading font-semibold text-white text-xl sm:ml-2">{eur(total)}</span>
          </p>
          <button
            type="submit"
            form="adhesion-form"
            disabled={!docsOk || dateInvalideCategorie}
            data-testid="sticky-pay"
            className="shrink-0 bg-rouge text-white font-heading uppercase tracking-wider text-xs sm:text-sm px-6 py-3 hover:bg-white hover:text-navy disabled:opacity-50 transition-[background-color,color] duration-300"
          >
            Récapitulatif & paiement
          </button>
        </div>
      </div>

      {lecture && (
        <div className="fixed inset-0 z-[60] bg-navy/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8" data-testid="document-modal" onClick={() => setLecture(null)}>
          <div className="bg-white w-full max-w-4xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-navy/10">
              <div className="min-w-0">
                <p className="font-heading uppercase font-semibold tracking-tight text-navy truncate" data-testid="document-modal-title">{lecture.title}</p>
                {lecture.version && <p className="text-xs text-steel">Version {lecture.version}</p>}
              </div>
              <button onClick={() => setLecture(null)} data-testid="document-modal-close" className="p-2 text-navy/60 hover:text-rouge transition-colors" aria-label="Fermer">
                <X size={20} />
              </button>
            </div>
            <iframe
              src={`${API_BASE}/api/documents/inscription/${lecture.id}/lire`}
              title={lecture.title}
              className="flex-1 w-full"
              data-testid="document-modal-frame"
            />
            <div className="px-5 py-3 border-t border-navy/10 text-xs text-steel">
              Après lecture, fermez cette fenêtre puis cochez la case de validation du document.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function InscriptionConfirme() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setFailed(true);
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/adhesion/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setData(data);
          return;
        }
      } catch {
        /* retry */
      }
      tries += 1;
      if (tries < 8) {
        timer = setTimeout(poll, 2000);
      } else {
        setFailed(true);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-[75vh] bg-navy flex items-center justify-center px-5 py-32">
      <SEO title="Inscription confirmée" />
      <div className="bg-navy2 border border-white/10 p-8 sm:p-12 max-w-2xl w-full" data-testid="inscription-confirmee">
        {!data && !failed && (
          <div className="text-center">
            <Loader2 className="animate-spin text-rouge mx-auto" size={36} />
            <p className="mt-6 text-white/70">Validation de votre inscription en cours…</p>
          </div>
        )}
        {data && (
          <>
            <span className="flex h-14 w-14 items-center justify-center bg-rouge text-white">
              <Check size={26} />
            </span>
            <h1 className="mt-6 font-heading uppercase font-semibold tracking-tight text-white text-3xl sm:text-5xl">
              Paiement reçu
            </h1>
            <p className="mt-4 text-white/80 font-semibold text-lg">
              Votre paiement a bien été reçu.
            </p>
            <p className="mt-2 text-white/60">
              Votre inscription est actuellement en cours de vérification par le Club Cycliste Le Boulou. Vous
              recevrez un email de confirmation dès que le club aura validé votre inscription.
            </p>
            <p className="mt-3 text-white/80 bg-white/5 border border-white/10 px-4 py-3 text-sm" data-testid="confirmation-compte-note">
              Votre espace personnel est disponible : un email de bienvenue vous permettra de définir
              votre mot de passe et de suivre l'état de votre inscription.
            </p>
            <div className="mt-7 border border-white/10 bg-navy p-6 text-sm space-y-2.5">
              <p className="flex justify-between gap-4 text-white/60"><span>Nom</span><span className="text-white font-semibold">{data.nom}</span></p>
              <p className="flex justify-between gap-4 text-white/60"><span>Prénom</span><span className="text-white font-semibold">{data.prenom}</span></p>
              {(data.lignes || []).map((l, i) => (
                <p key={i} className="flex justify-between gap-4 text-white/60"><span>{l.label}</span><span className="text-white font-semibold">{l.montant}</span></p>
              ))}
              <p className="flex justify-between gap-4 text-white/60 pt-2 border-t border-white/10"><span>Total payé</span><span className="text-rouge font-semibold text-lg">{data.total_label}</span></p>
              <p className="flex justify-between gap-4 text-white/60"><span>Date du paiement</span><span className="text-white font-semibold">{(data.paid_at || "").slice(0, 10)}</span></p>
              <p className="flex justify-between gap-4 text-white/60"><span>Statut</span><span className="text-amber-400 font-semibold uppercase text-xs tracking-widest">Paiement reçu — en attente de validation</span></p>
            </div>
            <div className="mt-7 border border-white/10 p-5">
              <p className="text-white/70 text-sm leading-relaxed">
                Les licences fédérales sont délivrées via les plateformes officielles. Finalisez votre démarche en
                ligne :
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                {data.licence_label.includes("FFC") && (
                  <a
                    href={FFC_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="platform-ffc"
                    className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-xs px-5 py-3 hover:bg-white hover:text-navy transition-[background-color,color] duration-300"
                  >
                    Accéder à la plateforme FFC <ExternalLink size={13} />
                  </a>
                )}
                {data.licence_label.includes("FSGT") && (
                  <a
                    href={FSGT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="platform-fsgt"
                    className="inline-flex items-center gap-2 bg-white text-navy font-heading uppercase tracking-wider text-xs px-5 py-3 hover:bg-rouge hover:text-white transition-[background-color,color] duration-300"
                  >
                    Accéder à la plateforme FSGT <ExternalLink size={13} />
                  </a>
                )}
              </div>
            </div>
          </>
        )}
        {failed && (
          <div className="text-center">
            <Loader2 className="text-white/40 mx-auto" size={36} />
            <h1 className="mt-6 font-heading uppercase font-semibold tracking-tight text-white text-2xl">Validation en cours</h1>
            <p className="mt-3 text-white/60 text-sm">
              Si vous avez bien payé, votre inscription sera confirmée sous quelques minutes par email.
            </p>
          </div>
        )}
        <Link
          to="/"
          className="mt-8 inline-block bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-white hover:text-navy transition-[background-color,color] duration-300"
        >
          Retour à l'accueil
        </Link>
      </div>
    </div>
  );
}

export function InscriptionAnnule() {
  return (
    <div className="min-h-[75vh] bg-navy flex items-center justify-center px-5 py-32">
      <SEO title="Inscription annulée" />
      <div className="bg-navy2 border border-white/10 p-8 sm:p-12 max-w-xl w-full text-center" data-testid="inscription-annulee">
        <span className="mx-auto flex h-14 w-14 items-center justify-center border border-white/20 text-white/70">
          <XCircle size={26} />
        </span>
        <h1 className="mt-6 font-heading uppercase font-semibold tracking-tight text-white text-3xl sm:text-4xl">
          Inscription non validée
        </h1>
        <p className="mt-4 text-white/60">
          Le paiement n'a pas abouti ou a été annulé. Votre inscription n'est pas validée tant que le paiement n'a pas
          été effectué. Aucun montant n'a été débité.
        </p>
        <p className="mt-2 text-white/60 text-sm">
          Votre inscription est conservée : vous pouvez reprendre votre paiement à tout moment, sans créer de doublon.
        </p>
        <Link
          to="/rejoindre"
          data-testid="inscription-reessayer"
          className="mt-8 inline-block bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-white hover:text-navy transition-[background-color,color] duration-300"
        >
          Reprendre mon paiement
        </Link>
      </div>
    </div>
  );
}
